import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import type {
  GetExpositionPoint,
  Reading,
} from "@services/apiEcms/apiEcms.types";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

const columnHelper = createColumnHelper<Reading>();

export const useReadingsColumns = (
  energy: "ELEC" | "GAZ",
  dials?: GetExpositionPoint["dials"],
) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const columns = useMemo(
    () => [
      columnHelper.accessor("date", {
        header: t("site.reading.columns.date"),
        enableSorting: false,
        meta: { align: "center" },
        cell: ({ getValue }) => {
          const date = getValue();
          return date
            ? t("date", {
                ns: "common",
                date: new Date(date),
                formatParams: {
                  date: {
                    dateStyle: "short",
                  },
                },
              })
            : t("unavailable", { ns: "common" });
        },
      }),
      ...(dials ?? []).map((dial) =>
        columnHelper.accessor("measures", {
          id: `measure-${dial.categoryShortName}`,
          header: dial.categoryShortName,
          enableSorting: false,
          meta: { align: "center" },
          cell: ({ getValue }) => {
            const index = getValue().find(
              (measure) => measure.category === dial.categoryShortName,
            )?.index;
            return index !== undefined
              ? t("site.reading.indexValue", { value: index, type: energy })
              : t("unavailable", { ns: "common" });
          },
        }),
      ),
      columnHelper.accessor("consumption", {
        header: t("site.reading.columns.consumption"),
        enableSorting: false,
        meta: { align: "center" },
        cell: ({ getValue }) =>
          t("site.reading.indexValue", {
            value: getValue(),
            type: "ELEC",
          }),
      }),
      ...(energy === "GAZ"
        ? [
            columnHelper.accessor("higherHeatingValue", {
              header: t("site.reading.columns.heatingValue"),
              enableSorting: false,
              meta: { align: "center" },
              cell: ({ getValue }) => {
                const heatingValue = getValue();
                return heatingValue
                  ? t("number", {
                      ns: "common",
                      value: heatingValue,
                    })
                  : t("unavailable", { ns: "common" });
              },
            }),
          ]
        : []),
    ],
    [t, energy, dials],
  );

  return columns;
};
